import React, { useEffect, useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';
import {
  buildCreatorMapStaticPlaceholderUrl,
  ensureCreatorMapStaticPlaceholderPreload,
  CREATOR_MAP_STATIC_WIDTH,
  CREATOR_MAP_STATIC_HEIGHT,
} from './staticMapPlaceholder';
import CREATOR_MAP_VIEWPORT_SX, {
  CREATOR_MAP_LIST_PANE_SX,
  CREATOR_MAP_MAP_COLUMN_INNER_SX,
  CREATOR_MAP_MAP_FRAME_SX,
  CREATOR_MAP_STATIC_IMG_STYLE,
} from './CreatorMapStyles';

function CreatorMapCardSkeleton() {
  return (
    <Box>
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{ width: '100%', aspectRatio: '1 / 1', borderRadius: '12px' }}
      />
      <Skeleton variant="text" animation="wave" sx={{ mt: 1.25, width: '70%' }} />
      <Skeleton variant="text" animation="wave" sx={{ width: '55%' }} />
      <Skeleton variant="text" animation="wave" sx={{ width: '40%' }} />
    </Box>
  );
}

/**
 * Suspense fallback for /creator-map.
 * Mirrors the final split layout (list skeletons | map) so swapping to CreatorMap
 * does not CLS. Static Maps image is the LCP element.
 */
export default function CreatorMapRouteFallback() {
  const staticMapUrl = useMemo(() => buildCreatorMapStaticPlaceholderUrl(), []);

  useEffect(() => {
    ensureCreatorMapStaticPlaceholderPreload(staticMapUrl);
  }, [staticMapUrl]);

  return (
    <Box
      data-testid="creator-map-route-fallback"
      sx={{
        ...CREATOR_MAP_VIEWPORT_SX,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        bgcolor: 'background.paper',
        boxSizing: 'border-box',
      }}
    >
      {/* Left list pane — skeletons match CreatorMapSimple card grid */}
      <Box
        aria-hidden
        sx={{
          width: { md: CREATOR_MAP_LIST_PANE_SX.width },
          maxWidth: { md: CREATOR_MAP_LIST_PANE_SX.maxWidth },
          minWidth: { md: CREATOR_MAP_LIST_PANE_SX.minWidth },
          flexShrink: 0,
          height: '100%',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          bgcolor: 'background.paper',
          px: 2,
          pt: { md: 4 },
          pb: 2,
          boxSizing: 'border-box',
        }}
      >
        <Skeleton variant="text" animation="wave" sx={{ width: '60%', height: 32, mb: 1.5 }} />
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{ width: '100%', height: 40, borderRadius: '12px', mb: 2 }}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 2,
            pt: 0.5,
          }}
        >
          <CreatorMapCardSkeleton />
          <CreatorMapCardSkeleton />
          <CreatorMapCardSkeleton />
          <CreatorMapCardSkeleton />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 2, md: 3 },
          pr: { xs: 2, md: 3 },
          pl: { xs: 2, md: 2 },
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={CREATOR_MAP_MAP_COLUMN_INNER_SX}>
          <Box
            id="google-map-placeholder"
            sx={CREATOR_MAP_MAP_FRAME_SX}
          >
            {staticMapUrl ? (
              <img
                src={staticMapUrl}
                alt=""
                width={CREATOR_MAP_STATIC_WIDTH}
                height={CREATOR_MAP_STATIC_HEIGHT}
                decoding="async"
                fetchpriority="high"
                style={CREATOR_MAP_STATIC_IMG_STYLE}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
